import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Select, Button, Tag, Spin, Empty, message } from 'antd';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { usePlatformState } from '../../../features/platform/platformStateSlice';
import { territoryApi } from '../../../features/territory/territoryApi';
import { useGetEciDetailQuery } from '../../../features/platform/platformApi';
import {
  useRunSyncMutation,
  useGetSyncJobsQuery,
  useLazyGetPcNamesTemplateQuery,
  useImportPcNamesMutation,
} from '../../../features/dataSync/dataSyncApi';

/* ------------------------------------------------------------------ *
 * Platform Admin — per-state ECI pull (onboarding Step 1, ECI card).
 * A trimmed version of the system-admin Data Pull page: ONLY the four
 * ECI-structure steps (State -> District -> Parliaments+Assemblies ->
 * Polling Stations), scoped to the selected platform state. No OCR,
 * no e-roll, no SEC ward rolls. Reuses the /access/data-sync/ APIs.
 * Route: /admin/eci-pull
 * ------------------------------------------------------------------ */

const STATUS_COLOR: Record<string, string> = {
  queued: 'default',
  running: 'processing',
  done: 'success',
  failed: 'error',
};

const KIND_LABELS: Record<string, string> = {
  state: 'States',
  district: 'Districts',
  ac: 'Parliaments + Assemblies',
  stations: 'Polling Stations',
};

export default function EciPullPage() {
  const { code: platformCode, name: stateName } = usePlatformState();

  // The backend resolves the platform state (e.g. "Rajasthan") to its ECI row
  // AUTHORITATIVELY and returns the id + code + already-synced counts, so we
  // don't re-map names on the client (that was fragile and could miss).
  const { data: eci, refetch: refetchEci } = useGetEciDetailQuery(platformCode, { skip: !platformCode });
  const stateId = eci?.state_id ?? undefined;
  const stateCode = eci?.state_code || '';
  const resolved = !!stateCode;

  // Parliament picker — needed only for the "Polling Stations" step.
  const { data: pcs = [], refetch: refetchPcs } = territoryApi.useGetParliamentsQuery(
    { state: stateId as number },
    { skip: stateId == null },
  );
  const [pcCode, setPcCode] = useState<string>('');

  // Parliament-name mapping (local → English): download sheet + upload filled.
  const [fetchPcTemplate, { isFetching: pcTplBusy }] = useLazyGetPcNamesTemplateQuery();
  const [importPcNames, { isLoading: pcImporting }] = useImportPcNamesMutation();
  const pcFileRef = useRef<HTMLInputElement>(null);

  const downloadPcTemplate = async () => {
    try {
      const blob = await fetchPcTemplate({ state: stateCode }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${stateCode || 'state'}-parliament-names.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Could not download the template.');
    }
  };

  const onPcFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const res = await importPcNames({ file: f }).unwrap();
      message.success(
        `Saved ${res.saved_count} parliament name${res.saved_count === 1 ? '' : 's'}` +
          (res.skipped ? `, skipped ${res.skipped}.` : '.'),
      );
      refetchPcs();
    } catch (err) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail;
      message.error(detail || 'Could not import the file.');
    }
  };

  // Already-synced counts come from the backend detail (authoritative + scoped).
  const counts: { label: string; value: number }[] = [
    { label: 'Districts', value: eci?.district ?? 0 },
    { label: 'Parliaments', value: eci?.parliament ?? 0 },
    { label: 'Assemblies', value: eci?.assembly ?? 0 },
    { label: 'Booths', value: eci?.booths ?? 0 },
  ];

  // Booths pulled per parliament (summed from the per-assembly counts) — shown
  // under the Polling Stations step so you see WHICH PCs already have booths.
  const pcBooths = (() => {
    const m = new Map<string, number>();
    (eci?.assemblies ?? []).forEach((a) => {
      if (a.pc && a.booths) m.set(a.pc, (m.get(a.pc) ?? 0) + a.booths);
    });
    return Array.from(m, ([pc, booths]) => ({ pc, booths }))
      .sort((a, b) => b.booths - a.booths);
  })();

  // Jobs — poll fast while something is running, slow when idle.
  const [pollMs, setPollMs] = useState(4000);
  const { data: jobs = [] } = useGetSyncJobsQuery(undefined, { pollingInterval: pollMs });
  const busy = jobs.some((j) => j.status === 'queued' || j.status === 'running');
  useEffect(() => { setPollMs(busy ? 3000 : 30000); }, [busy]);

  // When a running pull finishes (busy true -> false), tell the user and refresh
  // the counts/parliaments so the new data shows without a manual page reload.
  const wasBusy = useRef(false);
  useEffect(() => {
    if (wasBusy.current && !busy) {
      message.success('Pull finished — data refreshed.');
      refetchEci();
      refetchPcs();
    }
    wasBusy.current = busy;
  }, [busy, refetchEci, refetchPcs]);

  const [runSync, { isLoading: running }] = useRunSyncMutation();
  const isRunning = (kind: string, code?: string) =>
    jobs.some((j) => j.kind === kind && (code ? j.code === code : true)
      && (j.status === 'running' || j.status === 'queued'));

  const launch = async (kind: string, code: string | undefined, label: string) => {
    try {
      await runSync({ kind, code }).unwrap();
      message.success(`Started: ${label}${code ? ` (${code})` : ''}`);
    } catch (e) {
      const detail = (e as { data?: { detail?: string } })?.data?.detail;
      message.error(detail || 'Could not start the sync.');
    }
  };

  // The ECI-structure steps for THIS state, in order, each with the code it
  // needs. (The global "States" list is already in the DB, so it's not a step.)
  const steps: { kind: string; title: string; desc: string; code?: string; ready: boolean; have: number }[] = [
    { kind: 'district', title: 'Districts', desc: `Pulls all districts of ${stateName}.`, code: stateCode, ready: !!stateCode, have: eci?.district ?? 0 },
    { kind: 'ac', title: 'Parliaments + Assemblies', desc: 'Creates the parliamentary (PC) and assembly (AC) constituencies of the state.', code: stateCode, ready: !!stateCode, have: eci?.assembly ?? 0 },
    // Not a pull — ECI gives PC names in regional script, so map them to English
    // via a downloadable/uploadable sheet. Rendered specially below.
    { kind: 'pc_names', title: 'Parliament names (local → English)', desc: 'ECI returns parliament names in the local script. Download the sheet, fill the English names, and upload.', code: stateCode, ready: !!stateCode, have: 0 },
    { kind: 'stations', title: 'Polling Stations', desc: 'Pulls the booths of the chosen parliament.', code: pcCode, ready: !!pcCode, have: eci?.booths ?? 0 },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: 'All states', to: '/admin/overview' },
          { label: 'Onboarding', to: '/admin/onboarding' },
          { label: 'Import standard sources', to: '/admin/import-sources' },
          { label: 'Pull ECI data' },
        ]}
      />

      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-brand">PLATFORM ADMIN · ONBOARDING STEP 1 · ECI</span>
        <h1 className="text-2xl font-bold text-ink">Pull ECI data · {stateName}</h1>
        <p className="max-w-[760px] text-sm leading-6 text-ink-secondary">
          Pulls {stateName}'s electoral structure from the ECI website — states, districts,
          parliaments &amp; assemblies, and polling booths. Run the steps top-to-bottom. This
          pulls structure only (no voter rolls / OCR).
        </p>
      </div>

      {eci && !resolved && (
        <div className="rounded-lg border border-[#f1e3bd] bg-[#fffbeb] px-4 py-3 text-[13px] text-[#8a4b0f]">
          Couldn't match “{stateName}” to an ECI state, so its districts/booths can't be pulled. Check the state exists in the ECI data.
        </div>
      )}

      {/* Parliament picker (for Polling Stations) */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl bg-white p-5 shadow-card">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-ink-secondary">Parliament (for Polling Stations)</label>
          <Select
            style={{ minWidth: 300 }}
            size="large"
            showSearch
            optionFilterProp="label"
            placeholder={stateId == null ? 'Pull districts/assemblies first' : 'Choose a parliament…'}
            value={pcCode || undefined}
            onChange={(v) => setPcCode(v)}
            options={pcs.map((p) => ({ value: p.pc_code || String(p.id), label: p.name || p.pc_code }))}
            allowClear
            disabled={stateId == null || pcs.length === 0}
          />
        </div>
        <span className="pb-2 text-xs text-ink-secondary">State code: <b>{stateCode || '—'}</b></span>
      </div>

      {/* Current data already in the DB for this state */}
      <div className="rounded-xl bg-white p-6 shadow-card">
        <h2 className="mb-3 text-lg font-semibold text-ink">Current data · {stateName}</h2>
        {!eci ? (
          <Spin />
        ) : !resolved ? (
          <Empty description="State not found in ECI data." />
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {counts.map((c) => (
              <div key={c.label} className="rounded-lg border border-[#eef4ff] bg-[#f8fafc] p-4">
                <div className="text-2xl font-semibold text-ink">{c.value.toLocaleString('en-IN')}</div>
                <div className="text-xs uppercase tracking-wide text-[#64748b]">{c.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* The four pull steps */}
      <div className="rounded-xl bg-white p-6 shadow-card">
        <h2 className="mb-1 text-lg font-semibold text-ink">Pull the ECI structure</h2>
        <p className="mb-4 text-sm text-ink-secondary">Run these top-to-bottom.</p>
        <div className="flex flex-col divide-y divide-[#eef4ff]">
          {steps.map((step, i) => (
            <div key={step.kind} className="flex items-center gap-4 py-3">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-shade text-sm font-semibold text-brand">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-ink">{step.title}</span>
                  {step.have > 0 && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                      {step.have.toLocaleString('en-IN')} already
                    </span>
                  )}
                </div>
                <div className="text-xs text-ink-secondary">{step.desc}</div>
                {/* Polling Stations: show which parliaments already have booths. */}
                {step.kind === 'stations' && pcBooths.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {pcBooths.map((p) => (
                      <span key={p.pc} className="rounded bg-brand-shade px-2 py-0.5 text-[11px] font-medium text-brand">
                        {p.pc}: {p.booths.toLocaleString('en-IN')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {step.kind === 'pc_names' ? (
                <div className="flex shrink-0 items-center gap-2">
                  <input ref={pcFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onPcFile} />
                  <Button onClick={downloadPcTemplate} loading={pcTplBusy} disabled={!step.ready}>
                    Download sheet
                  </Button>
                  <Button type="primary" onClick={() => pcFileRef.current?.click()} loading={pcImporting} disabled={!step.ready}>
                    Upload filled
                  </Button>
                </div>
              ) : (
                <Button
                  type={step.kind === 'stations' ? 'primary' : 'default'}
                  disabled={!step.ready}
                  loading={running || isRunning(step.kind, step.code)}
                  onClick={() => launch(step.kind, step.code, step.title)}
                >
                  {step.have > 0 ? 'Re-pull' : step.ready ? 'Pull' : step.kind === 'stations' ? 'Pick a parliament' : 'State not found'}
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Recent activity */}
      <div className="rounded-xl bg-white p-6 shadow-card">
        <h2 className="mb-3 text-lg font-semibold text-ink">Recent activity</h2>
        {jobs.length === 0 ? (
          <Empty description="No jobs yet." />
        ) : (
          <div className="flex flex-col divide-y divide-[#eef4ff]">
            {jobs.slice(0, 8).map((j) => (
              <div key={j.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <span className="text-sm font-medium text-ink">{KIND_LABELS[j.kind] || j.kind}</span>
                  {j.code && <span className="ml-2 text-xs text-ink-secondary">{j.code_name || j.code}</span>}
                  {j.message && <div className="truncate text-xs text-ink-secondary">{j.message}</div>}
                </div>
                <Tag color={STATUS_COLOR[j.status] || 'default'}>{j.status}</Tag>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
